import { useEffect, useRef, useState } from "react";

export default function PromoSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = () => {
    const el = document.querySelector("#contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  if (dismissed) return null;

  return (
    <section
      ref={ref}
      className={`relative py-16 md:py-20 overflow-hidden transition-all duration-1000 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ background: "linear-gradient(135deg, #2a1f0e 0%, #3d2b10 40%, #2a1f0e 100%)" }}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #C9A84C, transparent)" }} />
        <div className="absolute -bottom-10 -right-10 w-80 h-80 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #C9A84C, transparent)" }} />
        {/* Stars */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#C9A84C] opacity-40 animate-pulse"
            style={{
              top: `${10 + (i * 7) % 80}%`,
              left: `${5 + (i * 13) % 90}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      {/* Close button */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#C9A84C]/50 hover:text-[#C9A84C] transition-colors duration-200 cursor-pointer rounded-full hover:bg-white/5"
        aria-label="Cerrar promoción"
      >
        <i className="ri-close-line text-lg"></i>
      </button>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/30 rounded-full px-5 py-2 mb-6">
          <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse"></span>
          <span
            className="text-[#C9A84C] text-xs tracking-[0.25em] uppercase font-medium"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Oferta especial · Tiempo limitado
          </span>
          <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse"></span>
        </div>

        {/* Headline */}
        <h2
          className="text-3xl md:text-5xl font-light text-white mb-4 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Tu primera lectura con un
          <br />
          <span className="italic text-[#C9A84C]">20% de descuento</span>
        </h2>

        {/* Subtext */}
        <p className="text-[#e8dcc8]/70 text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
          Descubre el poder del tarot por primera vez con una sesión personalizada a precio especial. 
          Solo tienes que mencionar el código <strong className="text-[#C9A84C] font-semibold">PRIMERA20</strong> al reservar.
        </p>

        {/* Divider with stars */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#C9A84C]/40"></div>
          <i className="ri-star-fill text-[#C9A84C] text-xs"></i>
          <i className="ri-star-fill text-[#C9A84C] text-base"></i>
          <i className="ri-star-fill text-[#C9A84C] text-xs"></i>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#C9A84C]/40"></div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handleScroll}
            className="bg-[#C9A84C] text-[#0d0a07] px-10 py-3 rounded-full text-sm font-semibold hover:bg-[#e0bc5e] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Reservar con descuento
          </button>
          <span className="text-[#e8dcc8]/40 text-xs">
            Válido para nuevos clientes · Sin fecha de caducidad
          </span>
        </div>
      </div>
    </section>
  );
}
